import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import CloakFetcher from 'cloak-auth/src/Client/CloakFetcher';
import { useAuthStore } from 'cloak-auth/src/Client/stores';
import { createIssue } from 'gen/client';
import { IssueCreateDTO } from 'gen/model';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { preload } from 'swr';

import { InstanceInfo, User } from '../../Types/AuthServer';
import { customPromiseToast, supportTicketPromise } from '../../Utils/toasts';

const problemTypes = ['Bug Report', 'Device Problem', 'Detection Problem', 'New Feature Request', 'Other'];

function getFirstAndLastName(fullName?: string): { firstName?: string; lastName?: string } | undefined {
    if (!fullName) {
        return undefined;
    }

    const nameParts: string[] = fullName.split(' ');

    if (nameParts.length < 2) {
        return undefined;
    }

    const firstName = nameParts[0];
    const lastName = nameParts.at(-1);

    return { firstName, lastName };
}

function useFetchAndSubmit() {
    const keycloakToken = useAuthStore.getState().decodedToken;
    const keycloakRoles = keycloakToken?.realm_access.roles;

    const fullName = getFirstAndLastName(keycloakToken?.name);

    const userData = {
        email: keycloakToken?.email,
        first_name: fullName?.firstName,
        issue_url: window.location.href,
        kc_roles: keycloakRoles ?? [],
        last_name: fullName?.lastName,
    };

    const fetchAndSubmit = async (formData: IssueCreateDTO) => {
        try {
            const fullData = {
                ...formData,
                ...userData,
            };

            await createIssue(fullData);
        } catch (error) {
            console.error('Error submitting form:', error);
            throw error; // Re-throw if needed to handle it in the caller
        }
    };

    return { fetchAndSubmit };
}

interface SupportTicketModalProps {
    addressfront?: boolean;
    onClose: () => void;
    open: boolean;
}

export default function SupportTicketModal({ addressfront, onClose, open }: Readonly<SupportTicketModalProps>) {
    const { fetchAndSubmit } = useFetchAndSubmit();
    const { control, handleSubmit, reset } = useForm<IssueCreateDTO>({
        defaultValues: {
            issue_description: '',
            issue_type: problemTypes[0],
        },
    });

    const keycloakToken = useAuthStore.getState().decodedToken;
    const keycloakRoles = keycloakToken?.realm_access.roles;

    const onSubmit: SubmitHandler<IssueCreateDTO> = async (data) => {
        if (addressfront) {
            try {
                await customPromiseToast(fetchAndSubmit(data), supportTicketPromise);
                reset();
                onClose();
            } catch (error) {
                console.error(error);
            }
        } else {
            try {
                const user = await preload<User>('users/timetableprofile/', CloakFetcher);
                const instanceInfo = await preload<InstanceInfo>('tasks/instance_info', CloakFetcher);
                const userData = {
                    admin: user.admin,
                    allow_tracking: user.allow_tracking,
                    companies: user.user_companies.map(({ dashboard, ...rest }) => rest),
                    email: user.email,
                    enable_days: user.enable_days,
                    first_name: user.first_name,
                    id: user.id,
                    issue_instance: instanceInfo.instance_role,
                    issue_url: window.location.href,
                    kc_roles: keycloakRoles ?? [],
                    last_name: user.last_name,
                    location_enabled: user.location_enabled,
                    perm_level: user.perm_level,
                    public: user.public,
                    public_calendar: user.public_calendar,
                    public_social: user.public_social,
                    push_enabled: user.push_enabled,
                    requests: user.requests,
                    social_groups: user.social_groups,
                    user_timezone: user.tz,
                    user_title: user.title,
                    username: user.username,
                    utc_end: user.utc_end,
                    utc_start: user.utc_start,
                };

                const fullData = {
                    ...data,
                    ...userData,
                };

                await customPromiseToast(createIssue(fullData as IssueCreateDTO), supportTicketPromise);
                reset();
                onClose();
            } catch (error) {
                console.error(error);
            }
        }
    };

    return (
        <Dialog fullWidth maxWidth="sm" onClose={onClose} open={open}>
            <DialogTitle>Submit a Support Ticket</DialogTitle>
            <DialogContent>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <FormControl fullWidth margin="normal">
                        <InputLabel id="problem-type-label">Problem Type</InputLabel>
                        <Controller
                            control={control}
                            name="issue_type"
                            render={({ field }) => (
                                <Select label="Problem Type" labelId="problem-type-label" {...field}>
                                    {problemTypes.map((type) => (
                                        <MenuItem key={type} value={type}>
                                            {type}
                                        </MenuItem>
                                    ))}
                                </Select>
                            )}
                        />
                    </FormControl>
                    <Controller
                        control={control}
                        name="issue_description"
                        render={({ field }) => (
                            <TextField
                                fullWidth
                                label="Problem Description"
                                margin="normal"
                                multiline
                                rows={4}
                                variant="outlined"
                                {...field}
                            />
                        )}
                    />
                    <Typography gutterBottom variant="body1">
                        By submitting this ticket, you allow us to use your email address to contact you if needed.
                    </Typography>
                    <DialogActions>
                        <Button onClick={onClose} variant="outlined">
                            Cancel
                        </Button>
                        <Button type="submit" variant="contained">
                            Submit
                        </Button>
                    </DialogActions>
                </form>
            </DialogContent>
        </Dialog>
    );
}
